import React, { createContext, useContext, useReducer } from 'react';
import { ChatState, ChatAction, LanguageCode } from '../types';

interface AppState extends ChatState {
  view: 'home' | 'chat';
}

type AppAction = ChatAction;

const initialState: AppState = {
  messages: [],
  isLoading: false,
  error: null,
  selectedLanguage: LanguageCode.EN,
  view: 'home',
};

const chatReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload], error: null };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_LANGUAGE':
      // Selecting a language navigates to the chat
      return { ...state, selectedLanguage: action.payload, messages: [], view: 'chat' };
    case 'CLEAR_CHAT':
      return { ...state, messages: [], error: null };
    case 'RESET_SESSION':
      return { ...initialState };
    default:
      return state;
  }
};

const ChatContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  return <ChatContext.Provider value={{ state, dispatch }}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChatContext must be used within ChatProvider');
  return context;
};
